import type { NbSignal } from "./jsonContracts";

// ── Guardar archivo: File System Access API (Chrome/Edge) con fallback ─────
async function saveFile(
  content: Uint8Array | string,
  mimeType: string,
  suggestedName: string,
): Promise<void> {
  const data = typeof content === "string"
    ? new TextEncoder().encode(content)
    : content;

  // Intentar File System Access API (Chrome 86+, Edge 86+)
  if ("showSaveFilePicker" in window) {
    try {
      const ext = suggestedName.split(".").pop() ?? "";
      const extMap: Record<string, { description: string; accept: Record<string, string[]> }> = {
        csv:  { description: "CSV",  accept: { "text/csv": [".csv"] } },
        json: { description: "JSON", accept: { "application/json": [".json"] } },
        xlsx: { description: "Excel", accept: { "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"] } },
        xls:  { description: "Excel", accept: { "application/vnd.ms-excel": [".xls", ".xlsx"] } },
      };
      const handle = await (window as any).showSaveFilePicker({
        suggestedName,
        types: ext in extMap ? [extMap[ext]] : [{ description: "Archivo", accept: { [mimeType]: [] } }],
      });
      const writable = await handle.createWritable();
      await writable.write(data);
      await writable.close();
      return;
    } catch (e) {
      // Usuario canceló o no soportado → fallback
      if ((e as Error).name === "AbortError") return;
    }
  }

  // Fallback: <a download>
  const blob = new Blob([data], { type: mimeType });
  const url  = URL.createObjectURL(blob);
  const a    = Object.assign(document.createElement("a"), { href: url, download: suggestedName });
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Helpers ────────────────────────────────────────────────────────────────
const esc = (v: string) => /[",\n]/.test(v) ? `"${v.replace(/"/g,'""')}"` : v;

/** Sanitiza un nombre de archivo quitando caracteres inválidos */
function sanitize(name: string): string {
  return name.replace(/[<>:"/\\|?*\x00-\x1f]/g, "_").trim() || "axon-tags";
}

// ── Exports públicos ───────────────────────────────────────────────────────
export async function exportSignalsCsv(rows: NbSignal[], baseName: string) {
  const hasSCL = rows.some(r => r.iedName);
  const header = hasSCL
    ? "Senal,Nodo_MMS,Referencia_IED,IEDname,Dataset_SCL"
    : "Senal,Nodo_MMS,Referencia_IED";
  const lines = [header, ...rows.map(r => hasSCL
    ? [r.signal, r.mmsNode, r.iedRef, r.iedName ?? "", r.dataset ?? ""].map(esc).join(",")
    : [r.signal, r.mmsNode, r.iedRef].map(esc).join(","))];
  await saveFile(lines.join("\n"), "text/csv", `${sanitize(baseName)}.csv`);
}

export async function exportSignalsJson(rows: NbSignal[], baseName: string) {
  await saveFile(
    JSON.stringify(rows, null, 2),
    "application/json",
    `${sanitize(baseName)}.json`,
  );
}

export async function exportSignalsXlsx(rows: NbSignal[], baseName: string) {
  const XLSX   = await import("xlsx");
  const hasSCL = rows.some(r => r.iedName);
  const hasConf = rows.some(r => r.score !== undefined);
  const data   = rows.map(r => {
    const obj: Record<string, string> = {
      "Nombre Señal":   r.signal,
      "Nodo MMS":       r.mmsNode,
      "Referencia IED": r.iedRef,
    };
    if (hasConf) obj["Confianza"] = r.score !== undefined ? `${(r.score * 100).toFixed(1)}%` : "";
    if (hasSCL) {
      obj["IEDname SCL"] = r.iedName ?? "";
      obj["Dataset SCL"] = r.dataset ?? "";
      obj["Estado"]      = r.status === "present" ? "Presente"
                         : r.status === "absent"  ? "Ausente" : "";
    }
    return obj;
  });
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Resultados");
  ws["!cols"] = Object.keys(data[0] ?? {}).map(k => ({
    wch: Math.max(k.length, ...data.map(r => String(r[k] ?? "").length), 10),
  }));
  const xlsxData = XLSX.write(wb, { bookType: "xlsx", type: "array" }) as Uint8Array;
  await saveFile(
    xlsxData,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    `${sanitize(baseName)}.xlsx`,
  );
}

// ── Transformación AB3 ─────────────────────────────────────────────────────
function transformAB3(mmsNode: string, signalName: string): string {
  const parts = mmsNode.split("/");
  let preDos: string, preMms: string;
  if (parts.length >= 3) {
    preDos = parts.slice(2).join("/");
    preMms = parts.slice(0, 2).join("/");
  } else if (parts.length === 2) {
    const lnPart = parts[1];
    const dotIdx = lnPart.indexOf(".");
    if (dotIdx >= 0) {
      preDos = lnPart.slice(dotIdx + 1);
      preMms = parts[0] + "/" + lnPart.slice(0, dotIdx);
    } else { preDos = lnPart; preMms = mmsNode; }
  } else { return mmsNode; }

  let dataObj: string;
  if (preDos.includes(".")) {
    const di  = preDos.indexOf(".");
    const bef = preDos.slice(0, di);
    const aft = preDos.slice(di + 1);
    dataObj   = `$MX$${bef}$${aft}$instCVal$mag$f`;
  } else if (preDos === "Op" || preDos === "Str") {
    dataObj = `$ST$${preDos}$general`;
  } else {
    dataObj = `$ST$${preDos}$stVal`;
  }
  if (signalName.trim().toLowerCase().startsWith("comando")) {
    dataObj = dataObj.replace("$ST$", "$CO$").replace("$stVal", "");
  }
  return preMms + dataObj;
}

function getType104(ab3Node: string): string {
  if (ab3Node.endsWith("$CO$Pos"))        return "DC";
  if (ab3Node.endsWith("$Pos$stVal"))     return "DP";
  if (ab3Node.endsWith("$stVal") || ab3Node.endsWith("$general")) return "SP";
  if (ab3Node.endsWith("$instCVal$mag$f")) return "ME FL";
  return "";
}

export async function exportAG800(rows: NbSignal[], baseName: string) {
  const XLSX = await import("xlsx");
  const data = rows.map(r => {
    const ab3Node = transformAB3(r.mmsNode, r.signal);
    return {
      "IED Name":      "",
      "Short Name":    r.signal,
      "Description":   "",
      "IEC61850 Node": ab3Node,
      "Type104":       getType104(ab3Node),
      "Addr104":       "",
    };
  });
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "AG800");
  ws["!cols"] = [
    { wch: 14 }, { wch: 35 }, { wch: 20 }, { wch: 50 }, { wch: 10 }, { wch: 12 },
  ];
  const xlsxData = XLSX.write(wb, { bookType: "xlsx", type: "array" }) as Uint8Array;
  await saveFile(
    xlsxData,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    `${sanitize(baseName)}_AG800.xlsx`,
  );
}
